import {Tooltip} from '@material-ui/core';
import {hexToRGB, SIENNA_300} from '@wandb/weave/common/css/globals.styles';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {Pill} from '@wandb/weave/components/Tag';
import React, {useEffect} from 'react';
import {useSelector} from 'react-redux';
import Timeago from 'react-timeago';

import {BlockNavigation} from '../../../components/BlockNavigation';
import {TeamMemberRole} from '../../../components/MembersTable/TeamMembersTable/utils';
import SaveDraftTrigger from '../../../components/SaveDraftTrigger';
import * as Analytics from '../../../services/analytics';
import * as ReportViewsSelectors from '../../../state/reports/selectors';
import * as ViewHooks from '../../../state/views/hooks';
import * as ReportActions from '../../../state/views/report/actions';
import {ReportWidthOption} from '../../../state/views/report/types';
import {isOnGalleryPostEdit} from '../../../util/urls';
import {useGrammarlyNoticeContext} from '../../../util/userContext';
import {useLoadedReportContext} from '../LoadedReportContext';
import {useReportPageContext} from '../ReportPageContext';
import * as S from '../ReportPageHeader/ReportPageHeader.styles';
import {ReportPageMenuActions} from '../ReportPageHeader/ReportPageMenuActions';
import ReportPanelSettings from './ReportPanelSettings';

type ReportPageHeaderProps = {
  reportWidth: ReportWidthOption;
  isTipsSidebarOpen: boolean;
  setIsTipsSidebarOpen: (isOpen: boolean) => void;
};

export const ReportPageEditHeader = ({
  reportWidth,
  isTipsSidebarOpen,
  setIsTipsSidebarOpen,
}: ReportPageHeaderProps) => {
  const {
    teamMemberInfo: {foundTeamMember},
    isPublicProject,
  } = useReportPageContext();
  const {reportViewRef, view} = useLoadedReportContext();

  const isViewOnlyTeamMember =
    foundTeamMember?.role === TeamMemberRole.VIEW_ONLY;

  const reportRef = useSelector(
    ReportViewsSelectors.getReportRef(reportViewRef)
  );

  const setReportWidth = ViewHooks.useViewAction(
    reportRef,
    ReportActions.setWidth
  );

  const setReportWidthAndResize = (width: ReportWidthOption) => {
    setReportWidth(width);
    Analytics.reportWidthOptionClicked({width, isPublicProject});
  };

  const onTipsSidebarClick = () => {
    setIsTipsSidebarOpen(!isTipsSidebarOpen);
    Analytics.reportTipsSidebarClicked({isPublicProject});
  };

  const {
    dismissed: grammarlyNoticeDismissed,
    dismiss: dismissGrammarlyNotice,
    loading: grammarlyNoticeContextLoading,
  } = useGrammarlyNoticeContext();
  const grammarlyDetected = document.querySelector('grammarly-popups') != null;
  const showGrammarlyNotice =
    grammarlyDetected &&
    !grammarlyNoticeContextLoading &&
    !grammarlyNoticeDismissed;

  useEffect(() => {
    if (showGrammarlyNotice) {
      Analytics.reportGrammarlyNoticeDisplayed({isPublicProject});
    }
  }, [isPublicProject, showGrammarlyNotice]);

  const isReportDraft = view.parentId == null;
  const isGalleryPostEdit = isOnGalleryPostEdit();

  return (
    <>
      <BlockNavigation
        when={view.saving}
        message="Your draft is still auto-saving. Leave anyway?"
      />
      <S.ReportPageHeader $isInEditMode={true} className="hide-on-print">
        <S.FlexRow>
          <S.FlexRow style={{gap: '8px'}}>
            <Tooltip title="Get started with reports">
              {/* Note: Button needs to be wrapped in <span> for tooltip to appear */}
              <span>
                <Button
                  active={isTipsSidebarOpen}
                  icon="lightbulb-info"
                  variant="secondary"
                  onClick={onTipsSidebarClick}
                />
              </span>
            </Tooltip>
            <ReportPanelSettings reportViewRef={reportViewRef} />
            <div style={{marginLeft: '24px'}}>
              <Tooltip title="Narrow page">
                <span>
                  <Button
                    active={reportWidth === 'readable'}
                    icon="content-narrow"
                    variant="secondary"
                    onClick={() => {
                      setReportWidthAndResize('readable');
                    }}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Wide page">
                <span>
                  <Button
                    active={reportWidth === 'fixed'}
                    icon="content-wide"
                    variant="secondary"
                    onClick={() => {
                      setReportWidthAndResize('fixed');
                    }}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Full-width page">
                <span>
                  <Button
                    active={reportWidth === 'fluid'}
                    icon="content-full-width"
                    variant="secondary"
                    onClick={() => {
                      setReportWidthAndResize('fluid');
                    }}
                  />
                </span>
              </Tooltip>
            </div>
          </S.FlexRow>
          <S.FlexRow>
            <p style={{marginLeft: '24px'}}>
              {view.saving ? (
                <>Autosaving...</>
              ) : (
                <>
                  Autosaved{' '}
                  <Timeago
                    minPeriod={10}
                    formatter={(
                      value,
                      unit,
                      suffix,
                      epochSeconds,
                      nextFormatter
                    ) =>
                      unit === 'second'
                        ? 'just now'
                        : nextFormatter!(value, unit, suffix, epochSeconds)
                    }
                    date={view.updatedAt}
                  />
                </>
              )}
            </p>
          </S.FlexRow>
        </S.FlexRow>
        <S.FlexRow style={{gap: '16px'}}>
          {isReportDraft && (
            <Pill
              color="sienna"
              icon="hide-hidden"
              label="Edits visible only to you"
            />
          )}
          {/* TODO - add loading state for this trigger */}
          <SaveDraftTrigger />
          {/* Can't share until you save */}
          {!isViewOnlyTeamMember && <ReportPageMenuActions viewerOwnsReport />}
        </S.FlexRow>
      </S.ReportPageHeader>
      {showGrammarlyNotice && (
        <div
          style={{
            background: hexToRGB(SIENNA_300, 0.48),
            padding: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          Grammarly and similar browser plugins can cause reports to be very
          slow. Please pause these extensions to improve performance.
          <Button
            variant="ghost"
            icon="close"
            onClick={dismissGrammarlyNotice}
          />
        </div>
      )}
    </>
  );
};
